import { useEffect, useMemo } from "react";
import moment from "moment";
import { s3baseUrl } from "src/config/config";
import {
  csvImage,
  docImage,
  pdfImage,
  wordImage,
  audioImage,
  xlsxImage,
  otherImage,
} from "src/assets";

export const IMAGE_EXTENSIONS = [
  "jpg",
  "JPG",
  "png",
  "webp",
  "jpeg",
  "JPEG",
  "PNG",
];

// File-type icons for attachments that are not photos.
export const FILE_ICONS = {
  docx: wordImage,
  mp3: audioImage,
  pdf: pdfImage,
  csv: csvImage,
  doc: docImage,
  xlsx: xlsxImage,
  xls: xlsxImage,
  other: otherImage,
};

// Ticket dates (`support_ticket_date`, `resolve_date`) are stored in the gym's clock marked as UTC:
// shown as stored, one format everywhere (list and detail), so the member sees the same time on both.
export const ticketDate = (date) =>
  date ? moment.utc(date).format("DD MMM YYYY, hh:mm A") : "";

// Ticket state → status pill (same words as before; colour by meaning). Used by the list and the detail.
// waiting = support has not replied yet (amber), Answered = support replied, the member's turn (teal),
// Closed = done (green), Trash = removed (red).
export const ticketStatus = (data) => {
  const ticket_status = data.ticket_status;
  const response_status = data.response_status;
  if (ticket_status === 0 && response_status == 1)
    return { pill: "due", text: "Answered" };
  if (ticket_status == 1) return { pill: "ok", text: "Closed" };
  if (ticket_status == 2) return { pill: "overdue", text: "Trash" };
  return { pill: "late", text: "waiting" };
};

// Saved files (a ticket's or a reply's) → GrAttachments tiles: photos show the smaller thumbnail and
// open the full file large; other files show their type icon and open in a new tab.
export const savedFileTiles = (images = []) =>
  (images || []).map((image, i) => {
    const ext = image.thumbnail_1.split(".").pop();
    const isImage = IMAGE_EXTENSIONS.includes(ext);
    return {
      key: i,
      src: isImage
        ? s3baseUrl + (image.thumbnail_2 || image.thumbnail_1)
        : FILE_ICONS[ext] || FILE_ICONS.other,
      name: image.thumbnail_1.split("/").pop(),
      image: isImage,
      href: s3baseUrl + image.thumbnail_1,
    };
  });

const extOf = (name = "") => name.split(".").pop();

// Files in a form (new ones from the picker, or saved images while editing) → GrAttachments tiles.
// Photo previews of new files are made once per change of `files` and freed afterwards (no new
// blob URL on every keystroke, no leak).
export const useComposerTiles = (files) => {
  const tiles = useMemo(
    () =>
      (files || []).map((file, index) => {
        if (file.type === "file") {
          const name = file.path?.name || "";
          const isImage = IMAGE_EXTENSIONS.includes(extOf(name));
          return {
            key: index,
            name,
            image: isImage,
            blob: isImage,
            src: isImage
              ? URL.createObjectURL(file.path)
              : FILE_ICONS[extOf(name)] || FILE_ICONS.other,
          };
        }
        const name = file.thumbnail_1?.split("/").pop() || "";
        const isImage = IMAGE_EXTENSIONS.includes(extOf(name));
        return {
          key: index,
          name,
          image: isImage,
          src: isImage
            ? s3baseUrl + (file.thumbnail_2 || file.thumbnail_1)
            : FILE_ICONS[extOf(name)] || FILE_ICONS.other,
        };
      }),
    [files]
  );
  useEffect(
    () => () =>
      tiles.forEach((tile) => {
        if (tile.blob) URL.revokeObjectURL(tile.src);
      }),
    [tiles]
  );
  return tiles;
};
